import React, { useEffect, useRef } from "react";
import { Formik, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import {
  loadStoreCallVisitPostRequest,
  loadStoreCallVisitGetRequest,
  loadStoreCallVisitGetVendorDataRequest,
} from "../redux/Actions";
import DefaultButton from "../../../shared/components/DefaultButton";
import FormikSelectField from "../../../shared/components/FormikSelectField";

const EVENT_TYPE_OPTIONS = ["Store Visit", "Store Call", "Device Shipment"];

const validationSchema = Yup.object({
  name: Yup.string().required("Name is required"),
  event_date: Yup.string().required("Date of visit is required"),
  event_type: Yup.string().required("Event type is required"),
    vendor_id: Yup.string().when("event_type", {
    is: (val) => val !== "Device Shipment",
    then: (schema) => schema.required("Vendor is required"),
    otherwise: (schema) => schema.notRequired(),
  }),
  tracking_url: Yup.string().when("event_type", {
    is: "Device Shipment",
    then: (schema) => schema.required("Tracking URL is required").url("Enter a valid URL"),
    otherwise: (schema) => schema.notRequired(),
  }),
  description: Yup.string().required("Description is required"),
});

const StoreCallVisitForm = ({ handleClose, onSuccess, storeSap, storeName, ticketNumber }) => {
  const dispatch = useDispatch();
  const {
    loading,
    success,
    error,
    storevisits: rawVisits,
    vendordata: rawVendors,   
    vendordataLoading,        
  } = useSelector((state) => state.searchsap);
  console.log("rawVendors →", rawVendors);

  // Get employee data from localStorage
  const emp_no = JSON.parse(localStorage.getItem("emp_no"));
  const employee = JSON.parse(localStorage.getItem("employee") || "{}");
  const emp_id = employee.id; // Primary key ID
  const hasSubmitted = useRef(false);

  const storevisits = Array.isArray(rawVisits)
    ? rawVisits
    : Array.isArray(rawVisits?.data)
    ? rawVisits.data
    : [];

  // normalize vendor list — adjust key names to match your API response
  const vendors = Array.isArray(rawVendors)
    ? rawVendors
    : Array.isArray(rawVendors?.data)
    ? rawVendors.data
    : [];

  const isTodayOrFuture = (eventDate) => {
    if (!eventDate) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const visitDate = new Date(eventDate);
    visitDate.setHours(0, 0, 0, 0);
    return visitDate >= today;
  };

  useEffect(() => {
    if (storeSap) {
      dispatch(loadStoreCallVisitGetRequest({ store_location_id: storeSap }));
    }
    dispatch(loadStoreCallVisitGetVendorDataRequest());   
  }, [storeSap]);

  useEffect(() => {
    if (success && hasSubmitted.current) {
      hasSubmitted.current = false;
      if (onSuccess) onSuccess();
      if (handleClose) handleClose();
    }
  }, [success]);

  const initialValues = {
    name: "",
    event_date: new Date().toISOString().split("T")[0],
    event_type: "Store Visit",
    vendor_id: "",
    tracking_url: "",    
    status: "",
    description: "",
    ticket_number: ticketNumber || "",
  };

  const handleSubmit = (values) => {
    hasSubmitted.current = true;
    const payload = {
      name: values.name,
      event_date: values.event_date,
      event_type: values.event_type,
      vendor_id: values.event_type !== "Device Shipment" ? values.vendor_id : null,
      tracking_url: values.event_type === "Device Shipment" ? values.tracking_url : null,
      status: "Completed",
      description: values.description,
      created_by: emp_id,
      ticket_number: values.ticket_number || null,
      web: true,
      locations: [
        {
          filter_type: "store",
          store_location_id: storeSap,
          employee_id: null,
        },
      ],
    };
    dispatch(loadStoreCallVisitPostRequest(payload));
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({ values, setFieldValue, handleSubmit }) => {
        const ticketFilter = ticketNumber || values.ticket_number;
        const futureVisits = storevisits.filter(
          (v) =>
            isTodayOrFuture(v.event_date) &&
            ticketFilter &&
            String(v.ticket_number).toLowerCase() === String(ticketFilter).toLowerCase()
        );

        return (
          <div className="p-2">

            {/* Store Name */}
            <div className="mb-2">
              <label className="form-label"><strong>Store Name</strong></label>
              <input
                type="text"
                className="form-control"
                value={storeName || storeSap || ""}
                readOnly
                style={{ backgroundColor: "#e9ecef", cursor: "not-allowed" }}
              />
            </div>

            {/* Ticket Number */}
            <div className="mb-2">
              <label className="form-label"><strong>Ticket Number</strong></label>
              <Field
                name="ticket_number"
                type="text"
                className="form-control"
                placeholder="Enter ticket number"
                disabled={!!ticketNumber}
                style={ticketNumber ? { backgroundColor: "#e9ecef", cursor: "not-allowed" } : {}}
              />
              <ErrorMessage name="ticket_number" component="div" className="text-danger small" />
            </div>

            {/* Reference table */}
            {futureVisits.length > 0 && (
              <div className="mb-3">
                <label className="form-label">
                  <strong>Upcoming Visits / Calls for Ticket #{ticketFilter} (Reference)</strong>
                </label>
                <div className="table-responsive" style={{ maxHeight: 125, overflowY: "auto" }}>
                  <table className="table table-sm table-bordered table-striped table-hover">
                    <thead className="table-secondary">
                      <tr>
                        <th>Ticket ID</th>
                        <th>Name</th>
                        <th style={{ whiteSpace: "nowrap" }}>Event Type</th>
                        <th style={{ whiteSpace: "nowrap" }}>Event Date</th>
                        <th>Status</th>
                        <th>Store</th>
                      </tr>
                    </thead>
                    <tbody>
                      {futureVisits.map((row) => {
                        const location = row.locations?.[0];
                        return (
                          <tr key={row.id}>
                            <td>
                              {row.ticket_id ? (
                                <a href={`/ticketing/${row.ticket_id}`} target="_blank" rel="noreferrer">
                                  {row.ticket_number ?? "-"}
                                </a>
                              ) : (
                                row.ticket_number ?? "-"
                              )}
                            </td>
                            <td>{row.name ?? "-"}</td>
                            <td style={{ whiteSpace: "nowrap" }}>{row.event_type ?? "-"}</td>
                            <td style={{ whiteSpace: "nowrap" }}>{row.event_date ?? "-"}</td>
                            <td>
                              <span
                                className={`badge ${
                                  row.status?.toLowerCase() === "completed"
                                    ? "bg-success"
                                    : row.status?.toLowerCase() === "pending"
                                    ? "bg-warning text-dark"
                                    : "bg-secondary"
                                }`}
                              >
                                {row.status ?? "-"}
                              </span>
                            </td>
                            <td>{location ? `${location.store_sap} - ${location.store_name}` : "-"}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Name */}
            <div className="mb-2">
              <label className="form-label"><strong>Name</strong></label>
              <Field
                name="name"
                type="text"
                className="form-control"
                placeholder="Enter visit name"
              />
              <ErrorMessage name="name" component="div" className="text-danger small" />
            </div>

            {/* Event Type */}
            <div className="mb-2">
              <label className="form-label"><strong>Event Type</strong></label>
              <FormikSelectField
                name="event_type"
                value={values.event_type}
                placeholder="Select event type"
                onChange={(name, val) => setFieldValue(name, val)}
              >
                {EVENT_TYPE_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </FormikSelectField>
              <ErrorMessage name="event_type" component="div" className="text-danger small" />
            </div>
            {values.event_type === "Device Shipment" && (
              <div className="mb-2">
                <label className="form-label"><strong>Tracking URL</strong></label>
                <Field
                  name="tracking_url"
                  type="url"
                  className="form-control"
                  placeholder="https://..."
                />
                <ErrorMessage name="tracking_url" component="div" className="text-danger small" />
              </div>
            )}         

            {/* Date */}
            <div className="mb-2" >
              <div>
                <label className="form-label"><strong>Date of Visit</strong></label>
                <Field name="event_date" type="date" className="form-control" />
                <ErrorMessage name="event_date" component="div" className="text-danger small" />
              </div>              
            </div>

            {/* 👇 Vendor Dropdown */}
            {values.event_type !== "Device Shipment" && (
              <div className="mb-2">
                <label className="form-label"><strong>Vendor</strong></label>
                <Field as="select" name="vendor_id" className="form-control">
                  <option value="">
                    {vendordataLoading ? "Loading vendors..." : "Select vendor"}
                  </option>
                  {vendors.map((v) => (
                    <option key={v.id} value={v.id}>{v.name}</option>
                  ))}
                </Field>
                <ErrorMessage name="vendor_id" component="div" className="text-danger small" />
              </div>
            )}

            {/* Description */}
            <div className="mb-3">
              <label className="form-label"><strong>Description</strong></label>
              <Field
                name="description"
                as="textarea"
                className="form-control"
                placeholder="Enter description"
                rows={4}
              />
              <ErrorMessage name="description" component="div" className="text-danger small" />
            </div>

            {/* Buttons */}
            <div className="d-flex justify-content-end gap-2 mt-3">
              <DefaultButton
                buttonClass="btn btn-secondary"
                buttonTitle="Cancel"
                buttonType="button"
                actionBlock={handleClose}
              />
              <DefaultButton
                buttonClass="default-btn-color"
                buttonTitle="Submit"
                buttonType="button"
                actionBlock={handleSubmit}
              />
            </div>

          </div>
        );
      }}
    </Formik>
  );
};

export default StoreCallVisitForm;